/**
 * Phase 0.5 Component F — wraps a button / link / interactive element
 * so it disables (with a tooltip) when the resolver blocks the action,
 * or hides entirely when the resource isn't visible at all.
 *
 * Usage (typical):
 *
 *   <ActionGate action="approve" resource={contractRef}>
 *     <Button onClick={onApprove}>Approve</Button>
 *   </ActionGate>
 *
 * Render rules, in order:
 *   1. viewMode === "no_view" — hidden (renders null).
 *   2. action listed in PolicyResult.actionBlocked — child rendered
 *      but its `disabled` prop is forced true and a tooltip is wrapped
 *      around it carrying the resolver's reason.
 *   3. decision === "deny" but action NOT explicitly blocked — also
 *      disabled (the resolver said deny for some other reason; safer
 *      to lock the action than to allow it).
 *   4. Otherwise — child rendered unchanged.
 *
 * The child must accept a `disabled` prop. Most UI primitives we use
 * (Button, IconButton, MenuItem) do.
 *
 * Loading is treated as fail-closed: the child is disabled until the
 * lookup resolves. Surfaces that want a different loading shape can
 * use `useResolveAction` directly.
 */ import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, isValidElement } from 'react';
import { useResolveAction } from './useResolveAction';
export function ActionGate({ action, resource, children, alsoDisabled = false, hideOnNoView = true }) {
    const { allowed, blocked, viewMode, isLoading, policy } = useResolveAction(resource, action);
    if (!/*#__PURE__*/ isValidElement(children)) {
        // Caller passed a fragment or text — render as-is. Surfaces with
        // these patterns should resourceactor; we don't crash on bad usage.
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    if (viewMode === 'no_view' && hideOnNoView) return null;
    const disabled = isLoading || !allowed || alsoDisabled;
    const reason = policy?.reason ?? '';
    const title = blocked ? `Action not permitted${reason ? `: ${reason}` : ''}` : children.props.title;
    return /*#__PURE__*/ cloneElement(children, {
        disabled,
        title
    });
}
